import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Percent, Info } from "@phosphor-icons/react";
import SEO, { faqSchema, calculatorSchema, breadcrumbSchema } from "@/components/SEO";
import RelatedCalculators from "@/components/RelatedCalculators";
import { getCalculatorBySlug } from "@/data/calculatorsData";

const faqItems = [
  { q: "What is TDS on crypto under Section 194S?", a: "Section 194S requires the buyer (or the exchange facilitating the trade) to deduct 1% TDS on the consideration paid for a virtual digital asset. This applies when the total transaction value exceeds Rs 50,000 in a financial year (Rs 10,000 for specified persons like those whose income is auditable)." },
  { q: "Who is responsible for deducting crypto TDS?", a: "When trading on an Indian crypto exchange, the exchange typically handles TDS deduction automatically. For peer-to-peer transactions, the buyer is responsible for deducting and depositing the TDS with the government." },
  { q: "Can I claim a refund of crypto TDS?", a: "Yes. TDS is an advance payment of tax. When you file your ITR, the TDS deducted is credited against your total tax liability. If your total tax payable is less than the TDS already deducted, you can claim a refund." },
  { q: "Does TDS apply to crypto-to-crypto swaps?", a: "Yes. Section 194S applies to any transfer of a VDA, including swapping one cryptocurrency for another. The buyer is required to deduct 1% TDS on the value of the consideration." },
  { q: "How do I verify TDS deducted on my crypto trades?", a: "TDS deducted by exchanges should reflect in your Form 26AS and Annual Information Statement (AIS) on the Income Tax portal. Check these regularly to ensure all TDS credits are correctly recorded." }
];

export default function TDSCalculator() {
  const [txnValue, setTxnValue] = useState("");
  const [txnCount, setTxnCount] = useState("");
  const [result, setResult] = useState(null);
  const calcData = getCalculatorBySlug("crypto-tds-calculator");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const calculate = () => {
    const value = parseFloat(txnValue) || 0;
    const count = parseInt(txnCount) || 1;
    const totalValue = value * count;
    const tds = totalValue * 0.01;
    const netReceived = totalValue - tds;
    setResult({ totalValue, tds, netReceived, perTxn: value * 0.01 });
  };

  const fmt = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n);

  return (
    <div data-testid="tds-calculator-page">
      <SEO
        title="Crypto TDS Calculator India | Section 194S TDS Calculator 2026"
        description="Free crypto TDS calculator for India. Calculate 1% TDS on crypto transactions under Section 194S. Know your TDS deduction before buying or selling crypto."
        path="/calculators/crypto-tds-calculator"
        keywords={calcData.tags}
        jsonLd={[
          faqSchema(faqItems),
          calculatorSchema(calcData),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Free Tools", path: "/tools" },
            { name: "TDS Calculator", path: "/calculators/crypto-tds-calculator" }
          ])
        ]}
      />

      <section className="bg-[#21324D] py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-sm uppercase tracking-[0.2em] text-[#A7A55C] mb-6">Free Tool</p>
            <h1 className="font-heading font-semibold text-4xl sm:text-5xl text-[#F5F5F2] leading-none tracking-tight mb-6" data-testid="calc-heading">
              Crypto TDS Calculator India
            </h1>
            <p className="font-body text-lg text-[#F5F5F2]/75 leading-relaxed">
              Calculate the 1% TDS deducted on your crypto transactions under Section 194S. Know exactly how much TDS will be deducted before you trade.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-[#F5F5F2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="bg-white border border-[#383A46]/10 p-8 sm:p-10" data-testid="calc-form">
                <h2 className="font-body font-medium text-lg text-[#21324D] mb-6">Enter transaction details</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
                  <div>
                    <label className="block font-body text-sm font-medium text-[#21324D] mb-2">Transaction Value (INR)</label>
                    <input type="number" value={txnValue} onChange={(e) => setTxnValue(e.target.value)} placeholder="e.g. 100000" className="w-full bg-[#F5F5F2] border border-[#383A46]/15 px-4 py-3 font-body text-sm text-[#21324D] focus:outline-none focus:border-[#A7A55C]" data-testid="calc-txn-value" />
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-[#21324D] mb-2">Number of Transactions</label>
                    <input type="number" value={txnCount} onChange={(e) => setTxnCount(e.target.value)} placeholder="e.g. 10" className="w-full bg-[#F5F5F2] border border-[#383A46]/15 px-4 py-3 font-body text-sm text-[#21324D] focus:outline-none focus:border-[#A7A55C]" data-testid="calc-txn-count" />
                  </div>
                </div>
                <button onClick={calculate} className="w-full bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-colors font-body font-semibold text-sm py-4 flex items-center justify-center gap-2" data-testid="calc-calculate-btn">
                  <Percent size={18} weight="bold" /> Calculate TDS
                </button>
              </div>

              {result && (
                <div className="bg-white border border-[#A7A55C]/30 p-8 sm:p-10 mt-6" data-testid="calc-result">
                  <h3 className="font-body font-medium text-lg text-[#21324D] mb-6">TDS Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-[#383A46]/10"><span className="font-body text-sm text-[#383A46]">Total Transaction Value</span><span className="font-body text-sm font-medium text-[#21324D]">Rs {fmt(result.totalValue)}</span></div>
                    <div className="flex justify-between py-2 border-b border-[#383A46]/10"><span className="font-body text-sm text-[#383A46]">TDS per Transaction (1%)</span><span className="font-body text-sm font-medium text-[#21324D]">Rs {fmt(result.perTxn)}</span></div>
                    <div className="flex justify-between py-3 bg-[#21324D] px-4 -mx-4 mt-4"><span className="font-body text-sm font-semibold text-[#F5F5F2]">Total TDS Deducted</span><span className="font-body text-base font-semibold text-[#A7A55C]">Rs {fmt(result.tds)}</span></div>
                    <div className="flex justify-between py-2"><span className="font-body text-sm text-[#383A46]">Net Amount After TDS</span><span className="font-body text-sm font-medium text-[#21324D]">Rs {fmt(result.netReceived)}</span></div>
                  </div>
                </div>
              )}
            </div>
            <div className="lg:col-span-5">
              <div className="bg-[#152F2C] p-8 mb-6">
                <Info size={20} weight="duotone" className="text-[#A7A55C] mb-4" />
                <h3 className="font-body font-medium text-base text-[#F5F5F2] mb-3">About Section 194S TDS</h3>
                <ul className="space-y-3">
                  <li className="font-body text-sm text-[#F5F5F2]/65 leading-relaxed">1% TDS on all crypto transactions above threshold</li>
                  <li className="font-body text-sm text-[#F5F5F2]/65 leading-relaxed">Threshold: Rs 50,000/year (Rs 10,000 for specified persons)</li>
                  <li className="font-body text-sm text-[#F5F5F2]/65 leading-relaxed">Exchanges deduct TDS automatically on Indian platforms</li>
                  <li className="font-body text-sm text-[#F5F5F2]/65 leading-relaxed">P2P buyers must deduct and deposit TDS themselves</li>
                  <li className="font-body text-sm text-[#F5F5F2]/65 leading-relaxed">TDS is credited against your total tax liability at ITR filing</li>
                </ul>
              </div>
              <div className="border border-[#383A46]/10 p-8 bg-white">
                <h3 className="font-body font-medium text-base text-[#21324D] mb-3">TDS compliance support</h3>
                <p className="font-body text-sm text-[#383A46]/70 leading-relaxed mb-5">BitGenix helps individuals manage TDS tracking and businesses integrate 194S compliance into their platforms.</p>
                <Link to="/contact" className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-colors font-body font-semibold text-sm px-6 py-3 inline-flex items-center gap-2" data-testid="calc-cta">
                  Get Expert Help <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-heading font-semibold text-2xl sm:text-3xl text-[#21324D] tracking-tight mb-10">Frequently Asked Questions</h2>
          {faqItems.map((item, i) => <FAQItem key={i} q={item.q} a={item.a} />)}
        </div>
      </section>

      <RelatedCalculators currentSlug="crypto-tds-calculator" />
    </div>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#383A46]/10">
      <button onClick={() => setOpen(!open)} className="w-full text-left py-5 flex items-start justify-between gap-4">
        <span className="font-body font-medium text-base text-[#21324D]">{q}</span>
        <span className="text-[#A7A55C] text-sm mt-1 flex-shrink-0">{open ? "−" : "+"}</span>
      </button>
      {open && <p className="font-body text-sm text-[#383A46] leading-relaxed pb-5 pr-8">{a}</p>}
    </div>
  );
}
